import { Centrifuge, UnauthorizedError } from 'centrifuge'
import { useEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'

import { useNotificationRealtime } from '@/Containers/CommunicationSection/NotificationContainer/Hooks/useNotificationRealtime'
import {
    useChatRealtime,
    isChatRealtimeEvent,
} from '@/Containers/CommunicationSection/ChatContainer/Hooks/useChatRealtime'
import { useChatPresence } from '@/Containers/CommunicationSection/ChatContainer/Hooks/useChatPresence'
import { AssignmentResource } from '@/Containers/AssignmentSection/AssignmentContainer/Resources/AssignmentResource'
import { notify } from '@/Ship/Helpers/notify'
import { useNotification } from '@/Ship/Hooks/useNotification'
import { userManager } from '@/Core/Managers'
import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'

// Centrifugo живе на centrifugo.${TENANT_DOMAIN}, а фронт — на самому TENANT_DOMAIN (traefik у
// docker-compose.*.yml), тож домен береться зі сторінки. VITE_BASE_DOMAIN — лише явне
// перевизначення: compose його більше не передає, і без fallback URL ставав centrifugo.undefined
const CENTRIFUGO_DOMAIN = import.meta.env['VITE_BASE_DOMAIN'] || window.location.hostname

interface CentrifugoConnectorProps {
    /**
     * Extra handler invoked on a UserNotification.Created publication, in
     * addition to the built-in notification-state update. Layouts pass a
     * domain-specific callback here (e.g. the driver layout refreshes its
     * assignment list) without this component depending on any container.
     */
    onUserNotificationCreated?: () => void
    /**
     * Opens an assignment from the toast's «Переглянути завдання». Defaults to
     * the CRM AssignmentResource card; the driver layout passes its own PWA
     * drawer instead (the CRM card is not the driver's view of a task).
     */
    onOpenAssignment?: (uuid: string) => void
}

/** Ключ попередження про втрату з'єднання: одне на сесію збою, закривається після відновлення */
const CONNECTION_WARNING_KEY = 'centrifugo-connection'

export const CentrifugoConnector = observer((props: CentrifugoConnectorProps = {}) => {
    const { onUserNotificationCreated: onExtra, onOpenAssignment } = props
    const { showInfo } = useNotification()
    const { onUserNotificationCreated } = useNotificationRealtime()
    const { onChatEvent } = useChatRealtime()
    const { attachPresence } = useChatPresence()

    // Keep the latest callbacks in a ref so the subscription effect does NOT
    // depend on their identity. The realtime hooks return fresh callbacks on
    // every render; if they were in the deps, the effect would
    // tear down and re-create the Centrifugo connection on each render and
    // realtime publications (e.g. the unread counter) would be lost until a
    // full page reload.
    const handlersRef = useRef({ showInfo, onUserNotificationCreated, onChatEvent, onExtra, onOpenAssignment })
    handlersRef.current = { showInfo, onUserNotificationCreated, onChatEvent, onExtra, onOpenAssignment }

    const connectionToken = userManager.centrifugoConnectionToken
    const subscriptionToken = userManager.centrifugoSubscriptionToken
    const userUuid = userManager.user?.uuid

    useEffect(() => {
        if (!userManager.isAuthorized) return

        if (!connectionToken || !subscriptionToken || !userUuid) {
            console.warn('[CentrifugoConnector] Missing required tokens or user UUID')
            return
        }

        const centrifugoUrl = `wss://centrifugo.${CENTRIFUGO_DOMAIN}/connection/websocket`

        const userChannel = `user.${userUuid}`

        // console.log('[CentrifugoConnector] Connecting to Centrifugo', {
        //     url: centrifugoUrl,
        //     channel: userChannel,
        //     hasConnectionToken: !!connectionToken,
        //     hasSubscriptionToken: !!subscriptionToken,
        // })

        // getToken: self-healing on token expiry. Centrifugo tokens live 1h
        // from login/refresh; with a STATIC token centrifuge-js treats error
        // 109 ("token expired") as unrecoverable and never reconnects — any
        // tab older than an hour silently lost realtime. With getToken the
        // SDK asks us for a fresh token instead: we refresh the session
        // (refresh_token endpoint issues new centrifugo tokens, see
        // AppConfig ?centrifugo=true) and hand the new token back.
        const refreshCentrifugoToken = async (pick: () => string | null): Promise<string> => {
            const existing = pick()
            const refreshed = await userManager.refreshAccessToken()
            const token = pick()
            if (!refreshed || !token || token === existing) {
                // Session could not be refreshed (refresh token expired after
                // 1h idle → UserManager already logged the user out).
                // UnauthorizedError is the centrifuge-js contract for "stop
                // retrying permanently" — a plain Error would mean "transient,
                // retry with backoff" and spam an error toast on every attempt.
                throw new UnauthorizedError('Session expired, relogin required')
            }
            return token
        }

        const centrifuge = new Centrifuge(centrifugoUrl, {
            token: connectionToken,
            getToken: () => refreshCentrifugoToken(() => userManager.centrifugoConnectionToken),
        })

        const sub = centrifuge.newSubscription(userChannel, {
            token: subscriptionToken,
            getToken: () => refreshCentrifugoToken(() => userManager.centrifugoSubscriptionToken),
        })

        sub.on('publication', (ctx) => {
            const data = ctx.data || {}

            // Chat events ({ type: 'message:new', data }) have their own
            // handler and must not fall through to the notification toast
            // below — otherwise every chat message would spam "Нове сповіщення".
            if (isChatRealtimeEvent(data)) {
                handlersRef.current.onChatEvent(data)
                return
            }

            // Notifications tied to an assignment get a clickable toast that
            // opens the assignment card (e.g. "Завдання завершено водієм").
            const linkOptions =
                data.entityCode === 'Assignment' && data.entityUuid
                    ? {
                          actionLabel: 'Переглянути завдання',
                          // Pass the resource OBJECT, not the string 'Assignment'.
                          // The bare key is never registered in resourceManager
                          // (only the derived 'Assignment.WithRequests' /
                          // '.WithoutRequests' are, and lazily per page), so a
                          // string here makes GetResourceTask return null and the
                          // card crashes with "Resource key is required". Mirrors
                          // how IndexPage opens an assignment card.
                          onClick: () => {
                              const openAssignment = handlersRef.current.onOpenAssignment
                              if (openAssignment) {
                                  openAssignment(data.entityUuid)
                                  return
                              }
                              OpenResourceCardTask({
                                  resource: AssignmentResource,
                                  primaryFieldValue: data.entityUuid,
                                  mode: 'view',
                              })
                          },
                      }
                    : undefined

            // Вхідне сповіщення — інформаційне (6с), а не «успіх»: воно не є результатом дії користувача
            handlersRef.current.showInfo(data.title || 'Нове сповіщення', data.message, linkOptions)

            if (data.event === 'UserNotification.Created') {
                handlersRef.current.onUserNotificationCreated()
                handlersRef.current.onExtra?.()
            }
        })

        // Втрата зв'язку: одне попередження замість помилки на кожну спробу перепідключення;
        // після відновлення підписки попередження прибираємо
        let connectionWarned = false
        sub.on('error', (err) => {
            console.warn('[CentrifugoConnector] Subscription error', err)
            if (connectionWarned) return
            connectionWarned = true
            notify.warning("Немає з'єднання з сервером сповіщень", {
                key: CONNECTION_WARNING_KEY,
                description: "Нові сповіщення з'являться, щойно з'єднання відновиться.",
            })
        })

        sub.on('subscribed', () => {
            if (!connectionWarned) return
            connectionWarned = false
            notify.close(CONNECTION_WARNING_KEY)
        })

        centrifuge.on('connected', () => {
            // console.log('[CentrifugoConnector] Connected to Centrifugo')
        })

        centrifuge.on('disconnected', () => {
            // console.log('[CentrifugoConnector] Disconnected from Centrifugo')
        })

        sub.subscribe()
        centrifuge.connect()

        // Presence-канал чату — друга підписка на цьому ж з'єднанні;
        // attachPresence стабільний (useCallback []), ефект не перезапускає
        const detachPresence = attachPresence(centrifuge)

        return () => {
            // console.log('[CentrifugoConnector] Cleaning up')
            detachPresence()
            sub.unsubscribe()
            centrifuge.disconnect()
        }
        // Only re-create the connection when the identity/credentials change
        // (login, token refresh) — not on every render.
    }, [connectionToken, subscriptionToken, userUuid, attachPresence])

    return null
})

CentrifugoConnector.displayName = 'CentrifugoConnector'
